import {NavigateFunction} from "react-router-dom";
import {getUserTokenOrRedirect} from "../../../shared/services/auth.service";
import {RecordDates} from "../../../shared/components/record/RecordKit";

/** When the client was created and last updated (SQ-480). */
export interface RecordDatesDto {
  client?: RecordDates;
}

/** The company's own rating and private review of a client. */
export interface ClientRatingDto {
  rating?: number | null;
  review?: string | null;
}

/** A request the server refused, with its status for the callers that read it. */
export class RequestError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

async function request<T>(navigate: NavigateFunction, method: string, path: string, body?: unknown): Promise<T> {
  const token = await getUserTokenOrRedirect(navigate);
  const response = await fetch(`${process.env.REACT_APP_ACCUSITE_API_BASE_URL}/api/v1/${path}`, {
    method,
    headers: {Authorization: `Bearer ${token}`, ...(body !== undefined ? {'Content-Type': 'application/json'} : {})},
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw new RequestError(`${method} ${path}: ${response.status} ${response.statusText}`, response.status);
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export const getClientRecordDates = (navigate: NavigateFunction, clientId: number) =>
  request<RecordDatesDto>(navigate, 'GET', `clients/${clientId}/record-dates`);

export const getClientRating = (navigate: NavigateFunction, clientId: number) =>
  request<ClientRatingDto>(navigate, 'GET', `clients/${clientId}/rating`);

/** Null clears the rating. */
export const setClientRating = (navigate: NavigateFunction, clientId: number, rating: number | null) =>
  request<ClientRatingDto>(navigate, 'PUT', `clients/${clientId}/rating`, {rating});

/** Null clears the review. */
export const setClientReview = (navigate: NavigateFunction, clientId: number, review: string | null) =>
  request<ClientRatingDto>(navigate, 'PUT', `clients/${clientId}/review`, {review});

/**
 * Whether anything is attached to this client. Appointments, jobs and invoices
 * all reach the client through an estimate, so the by-client estimate list
 * (every status) answers for all four.
 */
export const clientHasRecords = async (navigate: NavigateFunction, clientId: number) =>
  (await request<unknown[]>(navigate, 'GET', `estimates/by-client/${clientId}`)).length > 0;

/** Throws a RequestError: 400 when the client still has records. */
export const deleteClientRecord = (navigate: NavigateFunction, clientId: number) =>
  request<void>(navigate, 'DELETE', `clients/${clientId}`);
